import axios from 'axios';
import {
  LOGIN_MODE_DIRECT,
  LOGIN_MODE_SELECT_CONTEXT,
  REQUEST_TIMEOUT,
  SUCCESS_CODE,
  SESSION_EXPIRED_MESSAGE,
} from '@/constants/auth';
import type {
  CurrentShopInfo,
  CurrentUser,
  DirectLoginResult,
  LoginCompanyOption,
  LoginPayload,
  LoginResult,
  LoginShopOption,
  SelectContextLoginResult,
  SelectContextPayload,
  SessionPayload,
  TokenPayload,
  UpdatePasswordPayload,
  UserContext,
} from '@/types/auth';
import type { ApiResponse } from '@/services/http/types';

const authClient = axios.create({
  baseURL: API_BASE_URL || '',
  timeout: REQUEST_TIMEOUT,
});

// 认证接口不走全局 request，避免和刷新 token 拦截器产生循环依赖。
const unwrap = <T>(response: ApiResponse<T>) => {
  if (response.code !== SUCCESS_CODE) {
    throw new Error(response.message || SESSION_EXPIRED_MESSAGE);
  }

  return response.data;
};

type RecordValue = Record<string, any>;

const toNumber = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }

  return undefined;
};

const toStringArray = (value: unknown) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => String(item ?? '').trim())
    .filter(Boolean);
};

const buildAuthorizationHeader = (accessToken?: string) => {
  if (!accessToken) {
    return undefined;
  }

  // 兼容旧项目后端，Authorization 直接透传原始 token。
  return {
    Authorization: accessToken,
  };
};

const pickTokenSource = (payload: unknown): RecordValue => {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  const source = payload as RecordValue;
  return (source.data as RecordValue) || (source.token as RecordValue) || source;
};

const normalizeTokenPayload = (
  payload: unknown,
  fallback?: Partial<TokenPayload>,
): TokenPayload => {
  // 兼容旧后端 snake_case、新结构 camelCase，以及部分网关包一层 data 的情况。
  const source = pickTokenSource(payload);
  const accessToken =
    source.accessToken ||
    source.access_token ||
    source.token ||
    fallback?.accessToken ||
    '';
  const refreshToken =
    source.refreshToken ||
    source.refresh_token ||
    fallback?.refreshToken ||
    undefined;
  const expiresAt =
    toNumber(source.expiresAt) ??
    toNumber(source.expires_at) ??
    fallback?.expiresAt;

  if (!accessToken) {
    throw new Error('登录返回缺少 access_token');
  }

  return {
    accessToken: String(accessToken),
    refreshToken: refreshToken ? String(refreshToken) : undefined,
    expiresAt,
  };
};

const normalizeUserContext = (payload: unknown): UserContext | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const source = payload as RecordValue;
  const accountId = toNumber(source.accountId ?? source.account_id);
  const customerUserId = toNumber(
    source.customerUserId ?? source.customer_user_id,
  );
  const companyId = toNumber(source.companyId ?? source.company_id);
  const shopId = toNumber(source.shopId ?? source.shop_id);
  const companyName = String(
    source.companyName ?? source.company_name ?? '',
  ).trim();
  const shopName = String(source.shopName ?? source.shop_name ?? '').trim();

  if (
    accountId === undefined &&
    customerUserId === undefined &&
    companyId === undefined &&
    shopId === undefined &&
    !companyName &&
    !shopName
  ) {
    return undefined;
  }

  return {
    accountId,
    customerUserId,
    companyId,
    companyName: companyName || undefined,
    shopId,
    shopName: shopName || undefined,
  };
};

const normalizeCurrentShopInfo = (
  payload: unknown,
): CurrentShopInfo | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const source = payload as RecordValue;
  const id = toNumber(source.id);
  const name = String(source.name ?? '').trim();
  const logo = String(source.logo ?? '').trim();

  if (id === undefined && !name && !logo) {
    return undefined;
  }

  return {
    id,
    name: name || undefined,
    logo: logo || undefined,
  };
};

const normalizeLoginShopOption = (payload: unknown): LoginShopOption | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const source = payload as RecordValue;
  const shopId = toNumber(source.shopId ?? source.shop_id);
  const shopName = String(source.shopName ?? source.shop_name ?? '').trim();

  if (shopId === undefined) {
    return null;
  }

  return {
    shopId,
    shopName: shopName || `店铺 ${shopId}`,
  };
};

const normalizeLoginCompanyOption = (
  payload: unknown,
): LoginCompanyOption | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const source = payload as RecordValue;
  const customerUserId = toNumber(
    source.customerUserId ?? source.customer_user_id,
  );
  const companyId = toNumber(source.companyId ?? source.company_id);
  const companyName = String(
    source.companyName ?? source.company_name ?? '',
  ).trim();
  const shops = Array.isArray(source.shops)
    ? source.shops
        .map((item) => normalizeLoginShopOption(item))
        .filter((item): item is LoginShopOption => Boolean(item))
    : [];

  if (customerUserId === undefined || companyId === undefined) {
    return null;
  }

  return {
    customerUserId,
    companyId,
    companyName: companyName || `组织 ${companyId}`,
    shops,
  };
};

const normalizeCompanyOptions = (payload: unknown): LoginCompanyOption[] => {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload
    .map((item) => normalizeLoginCompanyOption(item))
    .filter((item): item is LoginCompanyOption => Boolean(item));
};

const normalizeCurrentUser = (
  payload: unknown,
  userContext?: UserContext,
): CurrentUser => {
  // 用户信息接口在旧项目里是 data.user + roles + permissions，这里统一拍平。
  const source =
    payload && typeof payload === 'object' ? (payload as RecordValue) : {};
  const user =
    (source.user as RecordValue) ||
    (source.currentUser as RecordValue) ||
    (source.current_user as RecordValue) ||
    source;
  const normalizedContext =
    userContext ||
    normalizeUserContext(source.userContext || source.user_context);
  const currentShop = normalizeCurrentShopInfo(source.shop);
  const roles = toStringArray(source.roles ?? user.roles);
  const permissions = toStringArray(source.permissions ?? user.permissions);
  const username = String(user.username ?? user.name ?? user.nickname ?? '');
  const nickname = String(user.nickname ?? user.name ?? user.username ?? '');

  return {
    id: user.id ?? user.user_id ?? user.admin_id ?? username,
    name: String(user.name ?? user.nickname ?? user.username ?? ''),
    username,
    nickname,
    avatar: user.avatar || user.head_img || user.headimg || undefined,
    phone: user.phone || user.mobile || undefined,
    email: user.email || undefined,
    departmentTitle:
      user.departmentTitle || user.department_title || undefined,
    accountId: normalizedContext?.accountId,
    customerUserId: normalizedContext?.customerUserId,
    companyId: normalizedContext?.companyId,
    companyName: normalizedContext?.companyName,
    shopId: currentShop?.id ?? normalizedContext?.shopId,
    shopName: currentShop?.name ?? normalizedContext?.shopName,
    shopLogo: currentShop?.logo,
    roles,
    permissions,
  };
};

const normalizeSessionPayload = (payload: unknown): SessionPayload => {
  const tokenPayload = normalizeTokenPayload(payload);
  const source =
    payload && typeof payload === 'object' ? (payload as RecordValue) : {};
  const userContext = normalizeUserContext(
    source.userContext || source.user_context,
  );
  const hasUserPayload =
    typeof source.user === 'object' ||
    typeof source.currentUser === 'object' ||
    typeof source.current_user === 'object' ||
    Array.isArray(source.roles) ||
    Array.isArray(source.permissions);

  if (!hasUserPayload) {
    return {
      ...tokenPayload,
      userContext,
    };
  }

  return {
    ...tokenPayload,
    userContext,
    currentUser: normalizeCurrentUser(
      source.currentUser || source.current_user || source,
      userContext,
    ),
  };
};

const normalizeSelectContextLoginResult = (
  payload: unknown,
): SelectContextLoginResult => {
  const source =
    payload && typeof payload === 'object' ? (payload as RecordValue) : {};
  const tempToken = String(source.tempToken ?? source.temp_token ?? '').trim();

  if (!tempToken) {
    throw new Error('登录返回缺少 temp_token');
  }

  return {
    loginMode: LOGIN_MODE_SELECT_CONTEXT,
    tempToken,
    companies: normalizeCompanyOptions(source.companies),
  };
};

const normalizeDirectLoginResult = (payload: unknown): DirectLoginResult => {
  return {
    loginMode: LOGIN_MODE_DIRECT,
    session: normalizeSessionPayload(payload),
  };
};

const normalizeLoginResult = (payload: unknown): LoginResult => {
  const source =
    payload && typeof payload === 'object' ? (payload as RecordValue) : {};
  const loginMode = String(
    source.loginMode ?? source.login_mode ?? LOGIN_MODE_DIRECT,
  ).trim();

  if (loginMode === LOGIN_MODE_SELECT_CONTEXT) {
    return normalizeSelectContextLoginResult(source);
  }

  return normalizeDirectLoginResult(source);
};

export const loginRequest = async (payload: LoginPayload) => {
  const { data } = await authClient.post<ApiResponse<unknown>>(
    '/api/v1/auth/login',
    payload,
  );
  return normalizeLoginResult(unwrap(data));
};

// 刷新 token 同时兼容两套协议：
// 1. 新接口：refresh_token
// 2. 旧接口：refresh=true + Authorization: token
export const refreshTokenRequest = async (options: {
  refreshToken?: string;
  accessToken?: string;
}) => {
  const { refreshToken, accessToken } = options;
  const { data } = await authClient.post<ApiResponse<unknown>>(
    '/api/v1/admin/user/refresh-token',
    refreshToken ? { refresh_token: refreshToken } : { refresh: true },
    {
      headers: buildAuthorizationHeader(accessToken),
    },
  );

  return normalizeTokenPayload(unwrap(data), {
    refreshToken,
    accessToken,
  });
};

export const getCurrentUserRequest = async (
  accessToken: string,
  userContext?: UserContext,
) => {
  const { data } = await authClient.get<ApiResponse<unknown>>(
    '/api/v1/admin/user/info',
    {
      headers: buildAuthorizationHeader(accessToken),
    },
  );

  return normalizeCurrentUser(unwrap(data), userContext);
};

export const startSelectContextRequest = async (accessToken: string) => {
  const { data } = await authClient.post<ApiResponse<unknown>>(
    '/api/v1/auth/select-context/start',
    undefined,
    {
      headers: buildAuthorizationHeader(accessToken),
    },
  );

  return normalizeSelectContextLoginResult(unwrap(data));
};

export const getSelectContextOptionsRequest = async (tempToken: string) => {
  const { data } = await authClient.get<ApiResponse<unknown>>(
    '/api/v1/auth/select-context/options',
    {
      headers: buildAuthorizationHeader(tempToken),
    },
  );

  const payload = unwrap(data) as RecordValue;
  return normalizeCompanyOptions(payload.companies);
};

export const selectContextRequest = async (
  payload: SelectContextPayload,
  tempToken: string,
) => {
  const { data } = await authClient.post<ApiResponse<unknown>>(
    '/api/v1/auth/select-context',
    {
      customer_user_id: payload.customerUserId,
      company_id: payload.companyId,
      shop_id: payload.shopId,
    },
    {
      headers: buildAuthorizationHeader(tempToken),
    },
  );

  return normalizeSessionPayload(unwrap(data));
};

export const updatePasswordRequest = async (
  payload: UpdatePasswordPayload,
  accessToken?: string,
) => {
  const { data } = await authClient.put<ApiResponse<Record<string, never>>>(
    '/api/v1/admin/user/update-password',
    payload,
    {
      headers: buildAuthorizationHeader(accessToken),
    },
  );

  return unwrap(data);
};

export const logoutRequest = async (accessToken?: string) => {
  const { data } = await authClient.post<ApiResponse<Record<string, never>>>(
    '/api/v1/auth/logout',
    undefined,
    {
      headers: buildAuthorizationHeader(accessToken),
    },
  );

  return unwrap(data);
};
